import { useContext } from "react";
import { useQuery, useQueryClient } from "../../../react-query";
import moment from "moment";
import { riskLimitsApi } from "../../../services/api";
import { DateContext } from "../../../context/DateContext";

const QUERY_KEY = "riskLimits";

/** Latest available valuation date (YYYY-MM-DD), from the app-wide DateContext. */
export function useLatestValuationDate() {
  const { data: dateData } = useContext(DateContext);
  return dateData?.latestValuationDate
    ? moment(dateData.latestValuationDate, "MMM DD, YYYY").format("YYYY-MM-DD")
    : null;
}

/** Every valuation date (YYYY-MM-DD) with data available, from DateContext. */
export function useAvailableValuationDates() {
  const { data: dateData } = useContext(DateContext);
  return dateData?.dates || [];
}

/**
 * Live exposure (data360-computed) vs. limit-threshold utilization by
 * strategy type, as of a given valuation date — defaults to the latest
 * available date when asOfDate isn't provided.
 */
export function useRiskLimitsUtilization(asOfDate) {
  const latestDate = useLatestValuationDate();
  const effectiveDate = asOfDate || latestDate;

  const query = useQuery(
    [QUERY_KEY, "utilization", effectiveDate],
    async () => {
      const response = await riskLimitsApi.getUtilization({
        start_date: effectiveDate,
        end_date: effectiveDate,
      });
      return response?.data || [];
    },
    {
      enabled: Boolean(effectiveDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  return { ...query, asOfDate: effectiveDate };
}

/**
 * Drill-down: exposure for one strategy type, broken out by strategy name
 * and investment code, as of the same as-of date as the utilization grid.
 */
export function useRiskLimitsUtilizationDetail(strategyTypeName, asOfDate, enabled = true) {
  return useQuery(
    [QUERY_KEY, "utilization-detail", strategyTypeName, asOfDate],
    async () => {
      const response = await riskLimitsApi.getUtilizationDetail({
        start_date: asOfDate,
        end_date: asOfDate,
        strategy_type_name: strategyTypeName,
      });
      return response?.data || [];
    },
    {
      enabled: Boolean(enabled && strategyTypeName && asOfDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Drill-down: VaR 95% / 99% per strategy name within one strategy type, from
 * Risk Monitor. Separate from the exposure detail so a slow or unavailable
 * VaR service never blocks the exposure table.
 */
export function useRiskLimitsStrategyVar(strategyTypeName, asOfDate, enabled = true) {
  return useQuery(
    [QUERY_KEY, "strategy-var", strategyTypeName, asOfDate],
    async () => {
      const response = await riskLimitsApi.getStrategyVar({
        start_date: asOfDate,
        end_date: asOfDate,
        strategy_type_name: strategyTypeName,
      });
      return { rows: response?.data || [], valuationDate: response?.valuation_date || null };
    },
    {
      enabled: Boolean(enabled && strategyTypeName && asOfDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

/**
 * Drill-down: VaR 95% / 99% per investment code within one strategy type, from
 * Risk Monitor — summed up to investment type for the SPAC view. Kept separate
 * so Risk Monitor being slow or down never blocks the exposure table.
 */
export function useRiskLimitsInvestmentVar(strategyTypeName, asOfDate, enabled = true) {
  return useQuery(
    [QUERY_KEY, "investment-var", strategyTypeName, asOfDate],
    async () => {
      const response = await riskLimitsApi.getInvestmentVar({
        start_date: asOfDate,
        end_date: asOfDate,
        strategy_type_name: strategyTypeName,
      });
      return { rows: response?.data || [], valuationDate: response?.valuation_date || null };
    },
    {
      enabled: Boolean(enabled && strategyTypeName && asOfDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

/**
 * Drill-down: portfolio greeks P&L per strategy name and investment code within
 * one strategy type, from Risk Monitor. Separate query so a slow or unavailable
 * Risk Monitor never blocks the exposure table.
 */
export function useRiskLimitsGreeksDetail(strategyTypeName, asOfDate, enabled = true) {
  return useQuery(
    [QUERY_KEY, "greeks-detail", strategyTypeName, asOfDate],
    () =>
      riskLimitsApi.getGreeksDetail({
        start_date: asOfDate,
        end_date: asOfDate,
        strategy_type_name: strategyTypeName,
      }),
    {
      enabled: Boolean(enabled && strategyTypeName && asOfDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

/**
 * SPAC PM approval levels (backend-computed from the day's AUM and the
 * Allocated SPAC Risk Capital) for the as-of date. Not retried: a failure just
 * leaves the PM approval option unavailable.
 */
export function useRiskLimitsPmApproval(asOfDate, enabled = true) {
  return useQuery(
    [QUERY_KEY, "pm-approval", asOfDate],
    () => riskLimitsApi.getPmApproval({ start_date: asOfDate, end_date: asOfDate }),
    {
      enabled: Boolean(enabled && asOfDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

/**
 * The PM approval inputs in force on asOfDate (default today):
 * { ratios_pct: number[], selected_index: number|null, effective_date: string|null }.
 */
export function usePmApprovalConfig(asOfDate) {
  return useQuery([QUERY_KEY, "pm-approval-config", asOfDate || null], () => riskLimitsApi.getPmApprovalConfig(asOfDate), {
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The strategy-type limits in force on asOfDate (default today). `data` is the row list;
 * `effectiveDate` is the date of the saved snapshot they come from (null = not dated).
 */
export function useRiskLimitsThresholds(asOfDate) {
  const query = useQuery(
    [QUERY_KEY, "thresholds", asOfDate || null],
    async () => {
      const response = await riskLimitsApi.getThresholds(asOfDate);
      return { rows: response?.data || [], effectiveDate: response?.effective_date || null };
    },
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
  return { ...query, data: query.data?.rows, effectiveDate: query.data?.effectiveDate ?? null };
}

/** Refetches utilization + thresholds — backs the page's manual "Refresh" button. */
export function useRefreshRiskLimits() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries([QUERY_KEY]);
}
